import * as Sentry from '@sentry/react'
import { Capacitor } from '@capacitor/core'
import okaoAxios from '../helpers/axios'
import { ensureAnonId, readAnonIdSync } from '../helpers/anon-id'
import { getExperimentBucket } from '../helpers/analytics-context'
import { getAccessToken } from '../helpers/auth-helper'
import { errorLog } from '../utils/log-helper'

interface QueuedEvent {
  eventId: string
  name: string
  ts: string
  props: Record<string, unknown>
  context: Record<string, unknown>
}

const STORAGE_KEY = 'rsly_event_queue'
const SESSION_KEY = 'rsly_analytics_session'
const MAX_BATCH = 50
const FLUSH_THRESHOLD = 20
const FLUSH_INTERVAL_MS = 5000
const MAX_QUEUE = 500

// Snake_case params the server promotes to typed columns expect camelCase keys.
const PROMOTED_KEYS: Record<string, string> = {
  restaurant_id: 'restaurantId',
  dish_id: 'dishId',
  recommendation_id: 'recommendationId',
  search_id: 'searchId',
  rank_position: 'rankPosition',
  utm_source: 'utmSource',
  utm_campaign: 'utmCampaign',
  gclid: 'gclid',
  model_version: 'modelVersion',
}

// Params lifted into the event context envelope instead of props.
const CONTEXT_KEYS = new Set(['platform', 'app_version', 'environment', 'screen_name', 'source'])

let queue: QueuedEvent[] = []
let started = false
let flushing = false

// Count of events dropped because the server rejected the batch with a 4xx. Exposed via
// getDroppedEventCount() so silent first-party data loss is observable instead of invisible.
let droppedEventCount = 0

/**
 * Number of analytics events dropped due to 4xx rejections since page load.
 */
export function getDroppedEventCount(): number {
  return droppedEventCount
}

function eventsEnabled(): boolean {
  return process.env.REACT_APP_EVENTS_ENABLED !== 'false'
}

function generateId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through to manual id
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/**
 * Shared per-tab session id, persisted in sessionStorage. Reused by popupAnalytics so all
 * first-party events correlate under a single session id.
 */
export function getSessionId(): string {
  try {
    let sessionId = sessionStorage.getItem(SESSION_KEY)
    if (!sessionId) {
      sessionId = generateId()
      sessionStorage.setItem(SESSION_KEY, sessionId)
    }
    return sessionId
  } catch {
    return ''
  }
}

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)))
  } catch {
    // storage full/unavailable — keep the in-memory queue, skip persistence
  }
}

function restore(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        queue = parsed
      }
    }
  } catch {
    // ignore corrupt persisted queue
  }
}

function init(): void {
  if (started) {
    return
  }
  started = true
  restore()
  setInterval(() => {
    void flushEvents()
  }, FLUSH_INTERVAL_MS)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Tab is being hidden/backgrounded — a normal async axios POST can be discarded before it
        // completes, so use a keepalive/beacon transport that survives page teardown.
        flushEventsBeacon()
      }
    })
  }
}

/**
 * Best-effort synchronous flush for the visibilitychange/unload path.
 *
 * A regular async `okaoAxios.post` is unreliable when the page is being torn down (the request can
 * be cancelled before it lands), silently losing first-party events. This sends the pending queue
 * with `keepalive` (or `navigator.sendBeacon` for anonymous requests) so the browser guarantees
 * delivery even after the document is gone. Falls back to the async flush when neither a synchronous
 * anon id nor a keepalive transport is available.
 */
export function flushEventsBeacon(): void {
  if (queue.length === 0) {
    return
  }
  const anonId = readAnonIdSync()
  const base = (okaoAxios.defaults.baseURL ?? '').replace(/\/$/, '')
  const url = `${base}/events`
  const batch = queue.slice(0, MAX_BATCH)
  const body = JSON.stringify({
    anonId,
    eaterId: readEaterId(),
    sentAt: new Date().toISOString(),
    events: batch,
  })

  // No synchronous anon id (e.g. native platform before ensureAnonId ran) — defer to the async
  // path which can await it, rather than sending an unstitched batch.
  if (!anonId) {
    void flushEvents()
    return
  }

  let sent = false
  try {
    const token = getAccessToken()
    if (typeof fetch === 'function') {
      // keepalive preserves the bearer header so authenticated events keep their eater_id and,
      // unlike sendBeacon, handles the cross-origin JSON request correctly.
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }
      void fetch(url, { method: 'POST', keepalive: true, headers, body }).catch(() => {
        /* fire-and-forget: nothing to do if the unload send fails */
      })
      sent = true
    } else if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      sent = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
    }
  } catch {
    sent = false
  }

  if (sent) {
    queue = queue.slice(batch.length)
    persist()
  } else {
    // Could not hand the batch to a keepalive transport — keep the events and let the timer retry.
    void flushEvents()
  }
}

/**
 * Enqueue an already-sanitized analytics event for first-party delivery to POST /events.
 *
 * Fire-and-forget: never throws. Events batch in memory + localStorage and flush on a timer,
 * at a size threshold, and when the tab is hidden. The backend stamps eater_id from the JWT
 * (attached automatically by the axios interceptor when authenticated).
 */
export function enqueueEvent(name: string, sanitized: Record<string, unknown>): void {
  if (!eventsEnabled()) {
    return
  }
  try {
    init()
    const props: Record<string, unknown> = {}
    const context: Record<string, unknown> = {
      platform: Capacitor.getPlatform(),
      sessionId: getSessionId(),
    }
    // Geo (city/region/country) is derived server-side from load-balancer edge headers — the
    // client no longer sends it.
    // Deterministic A/B bucket derived from the anon id.
    const experimentBucket = getExperimentBucket()
    if (experimentBucket) {
      context.experimentBucket = experimentBucket
    }
    if (sanitized.app_version) {
      context.appVersion = sanitized.app_version
    }
    if (sanitized.screen_name) {
      context.surface = sanitized.screen_name
    }
    if (sanitized.source) {
      context.source = sanitized.source
    }

    for (const [key, value] of Object.entries(sanitized)) {
      if (CONTEXT_KEYS.has(key)) {
        continue
      }
      props[PROMOTED_KEYS[key] ?? key] = value
    }

    queue.push({
      eventId: generateId(),
      name,
      ts: new Date().toISOString(),
      props,
      context,
    })
    if (queue.length > MAX_QUEUE) {
      queue = queue.slice(-MAX_QUEUE)
    }
    persist()
    if (queue.length >= FLUSH_THRESHOLD) {
      void flushEvents()
    }
  } catch (error) {
    errorLog('[events] enqueue failed', error)
  }
}

/**
 * The authenticated eater's real UUID (the eater-dimension primary key), read from the persisted
 * login. Sent alongside anonId so the server can stamp events.eater_id with an id that joins the
 * eater dataset — the JWT subject alone does not match the eater PK. Undefined when logged out;
 * the server ignores it on anonymous (JWT-less) requests.
 */
function readEaterId(): string | undefined {
  try {
    const raw = localStorage.getItem('login')
    if (!raw) return undefined
    const eaterId = (JSON.parse(raw) as { eaterDTO?: { eaterId?: string } })?.eaterDTO?.eaterId
    return typeof eaterId === 'string' && eaterId.length > 0 ? eaterId : undefined
  } catch {
    return undefined
  }
}

/**
 * Flush queued events to POST /events in batches, keeping unsent events for retry on failure.
 */
export async function flushEvents(): Promise<void> {
  if (flushing || queue.length === 0) {
    return
  }
  flushing = true
  try {
    const anonId = await ensureAnonId()
    const eaterId = readEaterId()
    while (queue.length > 0) {
      const batch = queue.slice(0, MAX_BATCH)
      try {
        await okaoAxios.post('/events', {
          anonId,
          eaterId,
          sentAt: new Date().toISOString(),
          events: batch,
        })
        queue = queue.slice(batch.length)
        persist()
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status
        if (typeof status === 'number' && status >= 400 && status < 500) {
          // Client error (e.g. 401 from an expired/mismatched bearer token, 400 bad batch).
          // Retrying the same batch can never succeed and would replay these events on every
          // flush — drop the batch and move on. (Fire-and-forget analytics; losing a batch is
          // acceptable, an infinite replay loop is not.)
          // Track dropped count + warn loudly so this data loss is visible, not silent.
          droppedEventCount += batch.length
          errorLog(
            `[events] flush rejected (${status}), dropping ${batch.length} event(s) ` +
              `(total dropped: ${droppedEventCount})`,
            error,
          )
          // errorLog is a no-op in production, so report the loss straight to Sentry (breadcrumb
          // for context on nearby errors + a warning issue) — first-party data loss must be
          // observable, not just visible in an in-memory counter.
          Sentry.addBreadcrumb({
            category: 'analytics',
            level: 'warning',
            message: `[events] flush rejected (${status}), dropped ${batch.length} event(s)`,
            data: { status, dropped: batch.length, totalDropped: droppedEventCount },
          })
          Sentry.captureMessage(
            `[events] first-party analytics batch dropped (${status}); ` +
              `total dropped this session: ${droppedEventCount}`,
            'warning',
          )
          queue = queue.slice(batch.length)
          persist()
          continue
        }
        // Transient failure (offline, 5xx, topic not provisioned) — keep events, retry next flush.
        errorLog('[events] flush failed, will retry', error)
        break
      }
    }
  } finally {
    flushing = false
  }
}
